import { ReactNode, lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Footer } from '@/components/Footer';
import { 
  Home, 
  Wrench, 
  BookOpen, 
  User, 
  History, 
  Shield, 
  Menu,
  LogOut,
  Zap,
  LogIn,
  UserPlus,
  Settings,
  ChevronRight,
  X,
  Sparkles,
  Terminal,
  Crown,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useFreeMode } from '@/hooks/useFreeMode';
import { Logo } from '@/components/Logo';
const logoImage = '/logo.png';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { AmbientLayers } from '@/components/intel/AmbientLayers';
import { LangBar } from '@/components/intel/LangBar';
import { PlanBadge } from '@/components/PlanBadge';
import { useCommandPalette } from '@/components/CommandPalette';
import { CommandHint } from '@/components/ui/operator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';

const InteractiveBackground = lazy(() =>
  import('@/components/InteractiveBackground').then((m) => ({ default: m.InteractiveBackground })),
);
const GlobalTicker = lazy(() =>
  import('@/components/intel/GlobalTicker').then((m) => ({ default: m.GlobalTicker })),
);
const CommandPalette = lazy(() =>
  import('@/components/CommandPalette').then((m) => ({ default: m.CommandPalette })),
);
const MobileBottomBar = lazy(() =>
  import('@/components/MobileBottomBar').then((m) => ({ default: m.MobileBottomBar })),
);

interface AppLayoutProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  showBackground?: boolean;
}

const navItems = [
  { icon: Home, label: 'Inicio', href: '/' },
  { icon: Wrench, label: 'Arsenal', href: '/arsenal' },
  { icon: BookOpen, label: 'Conocimiento', href: '/knowledge' },
  { icon: Shield, label: 'Conciencia', href: '/conciencia' },
];

export const AppLayout = ({ 
  children, 
  requireAuth = false,
  requireAdmin = false,
  showBackground = true
}: AppLayoutProps) => {
  const { user, profile, isAdmin, loading, tokensBalance, hasUnlimitedTokens } = useAuth();
  const { freeMode } = useFreeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [interactiveBackgroundReady, setInteractiveBackgroundReady] = useState(false);
  const [tickerReady, setTickerReady] = useState(false);
  const [mobileBarReady, setMobileBarReady] = useState(false);
  const { open: cmdOpen, setOpen: setCmdOpen } = useCommandPalette();

  useEffect(() => {
    if (!loading) {
      if (requireAuth && !user) {
        navigate('/auth');
      }
      if (requireAdmin && !isAdmin) {
        navigate('/');
      }
    }
  }, [user, isAdmin, loading, requireAuth, requireAdmin, navigate]);

  useEffect(() => {
    if (!showBackground) {
      setInteractiveBackgroundReady(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setInteractiveBackgroundReady(true);
    }, 180);

    return () => clearTimeout(timer);
  }, [showBackground]);

  useEffect(() => {
    const tickerTimer = window.setTimeout(() => setTickerReady(true), 700);
    const mobileBarTimer = window.setTimeout(() => setMobileBarReady(true), 900);

    return () => {
      clearTimeout(tickerTimer);
      clearTimeout(mobileBarTimer);
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // Wait only for auth (fast — already cached). Free mode hydrates in background.
  if (loading && (requireAuth || requireAdmin)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center animate-pulse">
              <Logo size="md" />
            </div>
            <div className="absolute inset-0 rounded-2xl border-2 border-primary/50 animate-ping" />
          </div>
          <p className="text-sm text-muted-foreground font-mono">Cargando...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) return null;
  if (requireAdmin && !isAdmin) return null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* A11y: skip-to-content link, visible on focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-3 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:font-mono focus:text-xs"
      >
        Saltar al contenido principal
      </a>
      <AmbientLayers />
      {showBackground && interactiveBackgroundReady ? (
        <Suspense fallback={null}>
          <InteractiveBackground />
        </Suspense>
      ) : null}
      
      {/* Header — Operator Console */}
      <div className="sticky top-0 z-50">
        <LangBar />
        <header className="border-b border-border bg-background/92 backdrop-blur-xl backdrop-saturate-150">

        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-7xl">
          <div className="flex h-[52px] items-center justify-between gap-3">
            {/* Left: Logo + Nav */}
            <div className="flex items-center gap-7 min-w-0">
              <Link to="/" className="flex items-center gap-2.5 group shrink-0">
                <span className="relative flex items-center justify-center w-[40px] h-[40px] sm:w-[36px] sm:h-[36px] shrink-0">
                  {/* Glow halo */}
                  <span aria-hidden className="absolute inset-0 bg-primary/25 blur-[10px] opacity-70 group-hover:opacity-100 transition-opacity" />
                  {/* Diagonal corner mark frame (decorative) */}
                  <span aria-hidden className="absolute inset-0 border border-primary/40 clip-corner-mark group-hover:border-primary transition-colors" />
                  <img
                    src={logoImage}
                    alt="LOWKEYX"
                    width={32}
                    height={32}
                    className="relative object-contain w-[30px] h-[30px] sm:w-[26px] sm:h-[26px] drop-shadow-[0_0_8px_hsl(var(--primary)/0.7)]"
                    loading="eager"
                    decoding="async"
                  />
                </span>
                <span className="font-display text-[18px] sm:text-[19px] tracking-[0.18em] text-foreground">
                  LOWKEY<span className="text-primary">X</span>
                </span>
              </Link>

              {/* Desktop Nav — mono underline rail */}
              <nav className="hidden lg:flex items-stretch h-[52px] -mb-px">
                {navItems.map((item, idx) => {
                  const isActive = location.pathname === item.href ||
                    (item.href !== '/' && location.pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        "relative h-full flex items-center gap-2 px-3.5 border-r border-border font-mono text-[9px] uppercase tracking-[0.16em] transition-colors",
                        idx === 0 && "border-l border-border",
                        isActive
                          ? "text-foreground bg-surface-2"
                          : "text-muted-foreground hover:text-foreground hover:bg-surface-2/60",
                      )}
                    >
                      <item.icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                      {item.label}
                      {isActive && (
                        <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-primary" />
                      )}
                    </Link>
                  );
                })}

                {user && location.pathname.startsWith('/arsenal') && (
                  <Link
                    to="/history"
                    className={cn(
                      "relative h-full flex items-center gap-2 px-3.5 border-r border-border font-mono text-[9px] uppercase tracking-[0.16em] transition-colors",
                      location.pathname === '/history'
                        ? "text-foreground bg-surface-2"
                        : "text-muted-foreground hover:text-foreground hover:bg-surface-2/60",
                    )}
                  >
                    <History className="w-3.5 h-3.5" strokeWidth={1.6} />
                    Historial
                  </Link>
                )}
              </nav>
            </div>

            {/* Right: Cmd + Auth + Actions */}
            <div className="flex items-center gap-2">
              {/* Command Palette trigger */}
              <button
                onClick={() => setCmdOpen(true)}
                className="hidden md:flex items-center gap-3 h-9 px-3 border border-border bg-surface-1 hover:border-border-strong text-muted-foreground hover:text-foreground transition-colors group"
                aria-label="Open command palette"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <span className="font-mono text-[11px] uppercase tracking-[0.18em]">Buscar / Ejecutar</span>
                <CommandHint keys={["⌘", "K"]} />
              </button>
              <button
                onClick={() => setCmdOpen(true)}
                className="md:hidden w-9 h-9 border border-border bg-surface-1 flex items-center justify-center"
                aria-label="Open command palette"
              >
                <Sparkles className="w-4 h-4 text-primary" />
              </button>

              {user ? (
                <>
                  {/* Token Display */}
                  {!freeMode && (
                    <div className="hidden sm:flex items-center gap-2 h-9 px-3 border border-primary/40 bg-primary/10">
                      <Zap className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-mono font-bold text-primary tabular-nums">
                        {hasUnlimitedTokens ? '∞' : tokensBalance}
                      </span>
                    </div>
                  )}

                  {/* Admin */}
                  {isAdmin && (
                    <Link to="/admin" className="hidden sm:block">
                      <button
                        className={cn(
                          "h-9 px-3 border border-destructive/60 text-destructive font-mono text-[11px] uppercase tracking-[0.16em] flex items-center gap-2 hover:bg-destructive/10 transition-colors",
                          location.pathname === '/admin' && "bg-destructive/10",
                        )}
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Admin
                      </button>
                    </Link>
                  )}

                  {/* User Menu - Hidden on mobile (shown in sidebar instead) */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="hidden md:flex relative w-10 h-10 rounded-full bg-primary/10 border border-primary/30 hover:bg-primary/20"
                      >
                        <User className="w-5 h-5 text-primary" />
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-terminal border-2 border-background" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 p-0">
                      {/* User Info Header */}
                      <div className="p-4 border-b border-border bg-muted/30">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{profile?.username ? `@${profile.username}` : profile?.email?.split('@')[0] || 'Usuario'}</p>
                            <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                          <PlanBadge />
                          {/* Token display in dropdown - Hidden when free mode is active */}
                          {!freeMode && (
                            <div className="flex items-center gap-1.5 text-primary">
                              <Zap className="w-4 h-4" />
                              <span className="font-mono font-bold text-sm">
                                {hasUnlimitedTokens ? '∞' : tokensBalance}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="p-2">
                        <DropdownMenuItem asChild>
                          <Link to="/account" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                              <Settings className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">Mi Cuenta</p>
                              <p className="text-xs text-muted-foreground">Configuración y perfil</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </Link>
                        </DropdownMenuItem>
                        
                        {isAdmin && (
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg">
                              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                                <Shield className="w-4 h-4 text-destructive" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm text-destructive">Panel Admin</p>
                                <p className="text-xs text-muted-foreground">Gestión de la plataforma</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </Link>
                          </DropdownMenuItem>
                        )}
                      </div>
                      
                      <DropdownMenuSeparator className="my-0" />
                      
                      <div className="p-2">
                        <DropdownMenuItem 
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer text-destructive focus:text-destructive rounded-lg"
                        >
                          <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                            <LogOut className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-sm">Cerrar sesión</span>
                        </DropdownMenuItem>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link to="/auth" className="hidden sm:block">
                    <button className="h-9 px-3 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground transition-colors">
                      Acceder
                    </button>
                  </Link>
                  <Link to="/auth">
                    <button className="h-9 px-4 bg-primary text-primary-foreground border border-primary font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-primary/90 transition-colors flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Registrarse</span>
                      <span className="sm:hidden">Iniciar</span>
                    </button>
                  </Link>
                </>
              )}

              {/* Mobile Menu Trigger */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden relative"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>
        {tickerReady ? (
          <Suspense fallback={null}>
            <GlobalTicker />
          </Suspense>
        ) : null}
      </div>

      {/* Advanced Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Slide-in Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 z-[101] w-[85vw] max-w-[320px] bg-gradient-to-b from-background via-background to-background/95 border-l border-primary/20 md:hidden overflow-hidden flex flex-col"
            >
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
              <div className="absolute bottom-20 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
              
              {/* Header */}
              <div className="relative p-4 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
                      <Terminal className="w-4 h-4 text-primary" />
                    </div>
                  <span className="font-display font-bold text-sm">
                    LOWKEY<span className="text-primary">X</span>
                  </span>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-8 h-8 rounded-lg bg-muted/50 hover:bg-muted flex items-center justify-center transition-colors"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* User Profile Section - Only when logged in */}
              {user && (
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block m-4 p-4 rounded-xl bg-muted/30 border border-border/50 hover:border-primary/30 hover:bg-muted/50 transition-all active:scale-[0.98]"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-11 h-11 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-terminal border-2 border-background" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {profile?.username ? (
                        <p className="font-medium text-sm text-foreground truncate">
                          @{profile.username}
                        </p>
                      ) : (
                        <p className="font-medium text-sm text-foreground truncate">
                          {profile?.email?.split('@')[0] || 'Usuario'}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                    <PlanBadge />
                    {!freeMode && (
                      <div className="flex items-center gap-1.5 text-primary">
                        <Zap className="w-3.5 h-3.5" />
                        <span className="font-mono text-xs font-bold">
                          {hasUnlimitedTokens ? '∞' : tokensBalance}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              )}

              {/* Guest Section - When not logged in */}
              {!user && (
                <div className="p-4 border-b border-border/50">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-amber-500/5 border border-amber-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Modo Invitado</p>
                        <p className="text-[10px] text-muted-foreground">Acceso limitado</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to="/auth"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex-1"
                      >
                        <Button size="sm" className="w-full bg-primary hover:bg-primary/90 text-xs h-8">
                          <UserPlus className="w-3 h-3 mr-1.5" />
                          Registrarse
                        </Button>
                      </Link>
                      <Link
                        to="/auth"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex-1"
                      >
                        <Button variant="outline" size="sm" className="w-full text-xs h-8">
                          <LogIn className="w-3 h-3 mr-1.5" />
                          Entrar
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  Navegación
                </p>
                <nav className="space-y-1">
                  {navItems.map((item, index) => {
                    const isActive = location.pathname === item.href || 
                      (item.href !== '/' && location.pathname.startsWith(item.href));
                    
                    return (
                      <motion.div
                        key={item.href}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all relative overflow-hidden",
                            isActive 
                              ? "bg-primary/15 text-primary" 
                              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          )}
                        >
                          {isActive && (
                            <motion.div
                              layoutId="activeNav"
                              className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"
                            />
                          )}
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center",
                            isActive ? "bg-primary/20" : "bg-muted/50"
                          )}>
                            <item.icon className={cn("w-4 h-4", isActive && "text-primary")} />
                          </div>
                          <span className="font-medium text-sm">{item.label}</span>
                          {isActive && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          )}
                        </Link>
                      </motion.div>
                    );
                  })}
                  
                  {/* History - only when logged in */}
                  {user && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: navItems.length * 0.05 }}
                    >
                      <Link
                        to="/history"
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all",
                          location.pathname === '/history'
                            ? "bg-primary/15 text-primary" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        )}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          location.pathname === '/history' ? "bg-primary/20" : "bg-muted/50"
                        )}>
                          <History className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-sm">Historial</span>
                      </Link>
                    </motion.div>
                  )}
                </nav>

                {/* Admin Section */}
                {isAdmin && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      Administración
                    </p>
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-destructive hover:bg-destructive/10 transition-all"
                    >
                      <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                        <Shield className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Panel Admin</span>
                      <Crown className="w-3 h-3 ml-auto text-amber-400" />
                    </Link>
                  </div>
                )}

                {/* Quick Actions */}
                {user && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      Acciones
                    </p>
                    <Link
                      to="/account"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                    >
                      <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
                        <Settings className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Configuración</span>
                    </Link>
                  </div>
                )}
              </div>

              {/* Footer - Fixed at bottom */}
              <div className="mt-auto p-4 border-t border-border/50 bg-background/80 backdrop-blur-sm">
                {user && (
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive/20 transition-all active:scale-[0.98]"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium text-sm">Cerrar sesión</span>
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main id="main-content" className="relative z-10 flex-1 pb-16 md:pb-0">
        {children}
      </main>

      {/* Footer */}
      <Footer />

      {/* Mobile Bottom Bar (only mobile) */}
      {mobileBarReady ? (
        <Suspense fallback={null}>
          <MobileBottomBar onCommandOpen={() => setCmdOpen(true)} />
        </Suspense>
      ) : null}

      {/* Command Palette */}
      {cmdOpen ? (
        <Suspense fallback={null}>
          <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
        </Suspense>
      ) : null}
    </div>
  );
};
